import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    
    // Don't automatically show error toasts - let components handle their own error messaging
    // const message = error.response?.data?.error || 'An error occurred';
    // toast.error(message);
    
    return Promise.reject(error);
  }
);

export const authAPI = {
  adminLogin: (credentials) => api.post('/auth/admin/login', credentials),
  redeemVoucher: (code) => api.post('/auth/redeem', { code }),
  getSession: () => api.get('/auth/me'),
  disconnect: () => api.post('/auth/disconnect'),
};

export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  
  getPlans: () => api.get('/admin/plans'),
  createPlan: (plan) => api.post('/admin/plans', plan),
  updatePlan: (planId, plan) => api.put(`/admin/plans/${planId}`, plan),
  deletePlan: (planId) => api.delete(`/admin/plans/${planId}`),
  
  // WireGuard Peers Management (replaces users)
  getWgPeers: () => api.get('/admin/wg-peers'),
  createWgPeer: (peer) => api.post('/admin/wg-peers', peer),
  updateWgPeer: (peerId, peer) => api.put(`/admin/wg-peers/${peerId}`, peer),
  deleteWgPeer: (peerId) => api.delete(`/admin/wg-peers/${peerId}`),
  generateWGConfig: (peerId) => api.post(`/admin/wg-peers/${peerId}/config`),
  
  // Legacy user endpoints (kept for compatibility)
  getUsers: () => api.get('/admin/users'),
  createUser: (user) => api.post('/admin/users', user),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  
  getVouchers: () => api.get('/admin/vouchers'),
  createVouchers: (data) => api.post('/admin/vouchers', data),
  updateVoucher: (voucherId, voucher) => api.put(`/admin/vouchers/${voucherId}`, voucher),
  deleteVoucher: (voucherId) => api.delete(`/admin/vouchers/${voucherId}`),
  disableVoucher: (voucherId) => api.patch(`/admin/vouchers/${voucherId}/disable`),
  
  getSessions: () => api.get('/admin/sessions'),
  updateSession: (sessionId, session) => api.put(`/admin/sessions/${sessionId}`, session),
  deleteSession: (sessionId) => api.delete(`/admin/sessions/${sessionId}`),
  kickSession: (sessionId) => api.post(`/admin/sessions/${sessionId}/kick`),
  
  getAuditLogs: () => api.get('/admin/audit-logs'),
  getMiradoreDevices: () => api.get('/admin/miradore/devices'),
  miradoreDeviceAction: (id, action) => api.post(`/admin/miradore/devices/${id}/${action}`),
  getMobileOverview: () => api.get('/admin/mobile/overview'),
  getMobileUsers: () => api.get('/admin/mobile/users'),
  getMobileUser: (id) => api.get(`/admin/mobile/users/${id}`),
  updateMobileUser: (id, data) => api.put(`/admin/mobile/users/${id}`, data),
  deleteMobileUser: (id) => api.delete(`/admin/mobile/users/${id}`),
  getMobileDevices: () => api.get('/admin/mobile/devices'),
  getMobileOrders: () => api.get('/admin/mobile/orders'),
};

// MDM (Mobile Device Management). All calls proxy through the hotspot backend to
// the mobile-platform-backend. When MDM_ENABLED is off, getFeatures reports false
// (so the menu hides) and the rest return 503.
export const mdmAPI = {
  getFeatures: () => api.get('/admin/features'),

  getDevices: () => api.get('/admin/mdm/devices'),
  createDevice: (device) => api.post('/admin/mdm/devices', device),
  getDevice: (id) => api.get(`/admin/mdm/devices/${id}`),
  retireDevice: (id) => api.delete(`/admin/mdm/devices/${id}`),

  enrollDevice: (id) => api.post(`/admin/mdm/devices/${id}/enroll`),
  getEnrollment: (id) => api.get(`/admin/mdm/devices/${id}/enrollment`),
  getStatus: (id) => api.get(`/admin/mdm/devices/${id}/status`),

  listPolicies: (deviceId) => api.get(`/admin/mdm/policies/${deviceId}`),
  createPolicy: (type, data) => api.post(`/admin/mdm/policies/${type}`, data),
  updatePolicy: (id, data) => api.put(`/admin/mdm/policies/${id}`, data),
  pushPolicy: (deviceId, type, config) => api.post(`/admin/mdm/policies/${type}`, { device_id: deviceId, config }),
  
  // Device commands
  sendCommand: (deviceId, command) => api.post(`/admin/mdm/devices/${deviceId}/command`, { command }),
};

export default api;
